import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Constants from "../Constants";
import useSortableData from "../components/customHook/SortableTable";
import CaseListTable from "../components/case-list/CaseListTable";
import { useProductContext } from "../contexts/ProductContext";
import usePaginatedList from "./usePaginatedList";
import useUserSetting from "./useUserSetting";
import usePolling from "./usePolling";
import useUser from "./useUser";
import { CREATE_REQUEST_STATUS, REQUEST_TYPES } from "@demandproai/shared";

const POLLING_INTERVAL = 10 * 1000; // 10 seconds

// a job older than this is treated as stuck so a never-settling request cannot poll forever
const MAX_POLLING_AGE_MS = 30 * 60 * 1000;

const SETTLED_REQUEST_STATUSES = [
   CREATE_REQUEST_STATUS.COMPLETED,
   CREATE_REQUEST_STATUS.FAILED,
   CREATE_REQUEST_STATUS.BAD_RECORD
];

// An Easy Mode case stays a draft until its chained demand starts, so the demand progress the check
// below reads does not exist yet. Nothing is pushed to the browser, so without this the row sits still
// through the whole agent run and only catches up on a manual reload.
const getIsRequestTooOld = (createdAt) => Date.now() - new Date(createdAt) > MAX_POLLING_AGE_MS;

// a missing or invalid createdAt gives NaN, which compares false, so the request still counts as running
const getHasRequestRunning = ({ caseItem, requestType }) => {
   const { status, createdAt } = caseItem?.createRequests?.[requestType] ?? {};

   return Boolean(status) && !SETTLED_REQUEST_STATUSES.includes(status) && !getIsRequestTooOld(createdAt);
};

const getHasUnsettledProgress = (caseItem) => {
   const progress = caseItem?.resultProgress;
   const progressFields = [
      progress?.policeReport_Progress,
      progress?.medicalRecords_progress,
      progress?.preMedicalRecords_progress,
      progress?.medicalBills_progress,
      progress?.demandLetter_progress
   ];
   return progressFields.some(field => field !== "Successful" && field !== "Failed");
};

/**
 * Determines if a case has any in-progress work across all processing stages.
 * @param {Object} caseItem - Case object with resultProgress and createRequests fields
 * @returns {boolean} True if a draft case has a virtual-user run younger than MAX_POLLING_AGE_MS, or a non-draft case has a demand request younger than MAX_POLLING_AGE_MS or any of the five progress fields neither Successful nor Failed
 */
const isCaseRunning = (caseItem) => {
   if (!caseItem) return false;
   if (caseItem.isDraftCase) return getHasRequestRunning({ caseItem, requestType: REQUEST_TYPES.VIRTUAL_USER });
   return getHasRequestRunning({ caseItem, requestType: REQUEST_TYPES.DEMAND }) || getHasUnsettledProgress(caseItem);
};

const useCaseList = () => {
   const { fetchAndStoreUser } = useUser(false)
   const [searchParams] = useSearchParams();
   const urlSearch = searchParams.get("search") || "";

   // A parent app can supply a resolver that picks its own table, or opts out by returning null; the
   // context is optional, so fall back to an empty object when no ProductContextProvider is mounted.
   const { getOverrideCaseListTable } = useProductContext() ?? {};

   const getCaseListTableOverride = useCallback(() => {
      if (!getOverrideCaseListTable) {
         return CaseListTable;
      }
      const parentCaseListTable = getOverrideCaseListTable();
      if (parentCaseListTable) {
         return parentCaseListTable;
      }
      return CaseListTable;
   }, [getOverrideCaseListTable]);

   const [isMyCases, setIsMyCases] = useUserSetting({
      key: "isMyCases",
      defaultValue: false,
   });
   const [persistedPage, setPersistedPage] = useUserSetting({
      key: "caseListPage",
      defaultValue: 1,
   });

   const extraParams = useMemo(() => ({ isMyCases }), [isMyCases]);

   const {
      items: casesList,
      isLoading: isCaseListLoading,
      currentPage,
      limit,
      totalCount,
      onSearchInputChange,
      setSearch,
      setCurrentPage,
      setLimit,
      refetch,
   } = usePaginatedList({
      endpoint: Constants.ApiUrl.case.getCases,
      extraParams,
      initialSearch: urlSearch,
      initialPage: Number(persistedPage) > 0 ? Number(persistedPage) : 1,
      selectItems: (data) => data.cases,
      onSuccess : fetchAndStoreUser,
      onPageChange: setPersistedPage,
   });

   const { items } = useSortableData(casesList);

   // Notification clicks navigate to /cases?search=<caseId> while CaseList is already mounted
   useEffect(() => {
      setSearch(urlSearch);
   }, [urlSearch, setSearch]);

   // nothing is pushed to the browser, so the poll is the only thing refreshing a row while a
   // virtual-user run or demand generates
   const hasRunningCase = useMemo(() => casesList.some(isCaseRunning), [casesList]);
   usePolling({
      intervalMs: POLLING_INTERVAL,
      callback: () => refetch({ showLoader: false, shouldSkipOnSuccess: true }),
      enabled: hasRunningCase,
   });

   return {
      items,
      currentPage,
      totalCount,
      limit,
      isMyCases,
      isCaseListLoading,
      setCurrentPage,
      onSearchInputChange,
      setIsMyCases,
      setLimit,
      getCases: refetch,
      getCaseListTableOverride,
   };
};

export default useCaseList;
